import React, {useState} from 'react';
import {OperatorLegals, OperatorLegalSettingsData} from "types/Operator";
import {Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import FieldHeader from "components/Form/FieldHeader";
import {Tab} from "@headlessui/react";
import TabButton from "components/TabButton";
import {GlobeIcon} from "@heroicons/react/outline";
import {concatenate} from "services/string";
import * as Yup from "yup";
import {TextAreaField} from "../../../components/Form/Form";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";

enum EntryMode {URL, TEXT}

type Props = {
    settings?: OperatorLegals | null
    onSubmit: (values: OperatorLegalSettingsData, helpers: FormikHelpers<OperatorLegalSettingsData>) => any
}

const modes: Record<"url" | "text", EntryMode> = {
    url: EntryMode.URL,
    text: EntryMode.TEXT
}

function LegalForm(props: Props) {
    const [privacyMode, setPrivacyMode] = useState<EntryMode>(modes[props.settings?.privacy_policy?.type ?? "url"]);
    const [termsMode, setTermsMode] = useState<EntryMode>(modes[props.settings?.terms_and_conditions?.type ?? "url"]);

    const validationSchema = Yup.object().shape({
        privacy_policy: privacyMode === EntryMode.URL
          ? Yup.string()
            .matches(
              /^https?:\/\//,
              'URL must start with http:// or https://'
            )
            .url('URL is not valid')
            .max(255, 'Maximum of 255 characters')
          : Yup.string().max(50_000, 'Maximum of 50,000 characters'),
        terms_and_conditions: termsMode === EntryMode.URL
          ? Yup.string()
            .matches(
              /^https?:\/\//,
              'URL must start with http:// or https://'
            )
            .url('URL is not valid')
            .max(255, 'Maximum of 255 characters')
          : Yup.string().max(50_000, 'Maximum of 50,000 characters'),
    })

    const initialValues: OperatorLegalSettingsData = {
        privacy_policy: props.settings?.privacy_policy?.value || '',
        terms_and_conditions: props.settings?.terms_and_conditions?.value || '',
    }
    
    function selectPrivacyMode(index: number, {setFieldValue}: FormikHelpers<any>) {
        setFieldValue('privacy_policy', '')
        setPrivacyMode(index)
    }

    function selectTermsMode(index: number, {setFieldValue}: FormikHelpers<any>) {
        setFieldValue('terms_and_conditions', '')
        setTermsMode(index)
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
            {formik => {
                return (
                    <section aria-labelledby="payment-details-heading">
                        <Form onKeyDown={event => {if(event.ctrlKey && event.key === 'Enter') formik.handleSubmit()}}>
                            <ErrorNotification formik={formik} />

                            <div className="shadow sm:rounded-md sm:overflow-hidden">
                                <div className="bg-white py-6 px-4 sm:p-6">
                                    <div>
                                        <h2 id="payment-details-heading" className="text-lg leading-6 font-medium text-gray-900">
                                            Legal Information
                                        </h2>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Set out the standard legal information to be displayed to your audience
                                        </p>
                                    </div>
    
                                    <div className="mt-6 grid grid-cols-6 gap-6">
    
                                        <div className={concatenate(privacyMode === EntryMode.URL? 'sm:col-span-4' : null, 'col-span-6')}>
                                            <FieldHeader label="Privacy Policy" />
                                            <Tab.Group selectedIndex={privacyMode} onChange={index => selectPrivacyMode(index, formik)}>
                                                <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                    <TabButton position={TabButton.positions.FIRST}>URL</TabButton>
                                                    <TabButton position={TabButton.positions.LAST}>Text</TabButton>
                                                </Tab.List>
                                                <Tab.Panels>
                                                    <Tab.Panel>
                                                        <Field
                                                            type="text"
                                                            icon={GlobeIcon}
                                                            name="privacy_policy"
                                                            id="privacy_policy"
                                                            maxLength={255}
                                                        />
                                                    </Tab.Panel>
                                                    <Tab.Panel>
                                                        <TextAreaField
                                                            name="privacy_policy"
                                                            id="privacy_policy"
                                                            rows={8}
                                                            maxChars={50_000}
                                                        />
                                                    </Tab.Panel>
                                                </Tab.Panels>
                                            </Tab.Group>
                                        </div>
    
                                        <div className={concatenate(termsMode === EntryMode.URL? 'sm:col-span-4' : null, 'col-span-6')}>
                                            <FieldHeader label="Terms and Conditions" />
                                            <Tab.Group selectedIndex={termsMode} onChange={index => selectTermsMode(index, formik)}>
                                                <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                    <TabButton position={TabButton.positions.FIRST}>URL</TabButton>
                                                    <TabButton position={TabButton.positions.LAST}>Text</TabButton>
                                                </Tab.List>
                                                <Tab.Panels>
                                                    <Tab.Panel>
                                                        <Field
                                                            type="text"
                                                            icon={GlobeIcon}
                                                            name="terms_and_conditions"
                                                            id="terms_and_conditions"
                                                            maxLength={255}
                                                        />
                                                    </Tab.Panel>
                                                    <Tab.Panel>
                                                        <TextAreaField
                                                            name="terms_and_conditions"
                                                            id="terms_and_conditions"
                                                            rows={8}
                                                            maxChars={50_000}
                                                        />
                                                    </Tab.Panel>
                                                </Tab.Panels>
                                            </Tab.Group>
                                        </div>
                                    </div>
                                </div>
    
                                <FormControls formik={formik} compact withoutCancel />
                            </div>
                        </Form>
                    </section>
               )
            }}
        </Formik>
    );
}

export default LegalForm;